import { useEffect, useRef, useState } from "react";
import SuccessModal from "../../../common/SuccessModal";
import useReassignClassroomTeacher from "../../../../hooks/teacher/classroom/useReassignClassroomTeacher";
import { useGetTeachers } from "../../../../hooks/school/teacher/useGetTeachers";
import { useDebounce } from "../../../../utils/useDebounce";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";

/**
 * @typedef {Object} ReassignTeacherModalProps
 * @property {boolean} show - Controls modal visibility
 * @property {() => void} onHide - Called when modal should close
 * @property {number|string} classroomId - The classroom to reassign
 * @property {number|string} [currentTeacherId] - ID of the currently assigned teacher (excluded from the list)
 * @property {() => void} [onReassignSuccess] - Callback after a successful reassign (e.g. refresh classroom data)
 */

/**
 * Self-contained modal to reassign a classroom to a different teacher.
 * Uses SuccessModal as the shell and passes the teacher picker + warning via children.
 *
 * @param {ReassignTeacherModalProps} props
 */
const ReassignTeacherModal = ({ show, onHide, classroomId, currentTeacherId, onReassignSuccess }) => {
  const { reassignTeacher, loading, error, success, reset } =
    useReassignClassroomTeacher();

  const {
    loading: teacherLoading,
    teachers,
    getTeachers,
    metaData: teacherMeta,
  } = useGetTeachers();

  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [teacherPage, setTeacherPage] = useState(1);

  const dropdownRef = useRef(null);
  const observerTarget = useRef(null);

  const isConfirming = useRef(false);

  useEffect(() => {
    if (!show) return;
    reset();
    setSelectedTeacher(null);
    setSearch("");
    setTeacherPage(1);
    setDropdownOpen(false);
    isConfirming.current = false;
    getTeachers(1, ITEMS_PER_PAGE, false, "");
  }, [show]);

  useEffect(() => {
    if (!show) return;
    setTeacherPage(1);
    getTeachers(1, ITEMS_PER_PAGE, false, debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !teacherLoading &&
          teacherMeta &&
          teacherMeta.current_page < teacherMeta.last_page
        ) {
          const next = teacherPage + 1;
          setTeacherPage(next);
          getTeachers(next, ITEMS_PER_PAGE, true, debouncedSearch);
        }
      },
      { threshold: 0.1, rootMargin: "50px" },
    );

    observer.observe(target);
    return () => observer.unobserve(target);
  }, [teacherLoading, teacherMeta, teacherPage, debouncedSearch]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);


  const handleClose = () => {
    if (loading || isConfirming.current) return;
    onHide();
  };

  const handleConfirm = async () => {
    if (!selectedTeacher || loading) return;
    isConfirming.current = true;
    const ok = await reassignTeacher({
      classroomId,
      teacherId: selectedTeacher.id,
    });
    isConfirming.current = false;
    if (ok && onReassignSuccess) {
      onReassignSuccess();
    }
  };

  const teacherName = [
    selectedTeacher?.firstName || selectedTeacher?.name || "",
    selectedTeacher?.lastName || "",
  ]
    .join(" ")
    .trim();

  if (success) {
    return (
      <SuccessModal
        showModal={show}
        handleClose={handleClose}
        type="success"
        title="Teacher Reassigned!"
        message={`The classroom has been successfully reassigned to ${teacherName}.`}
        primaryButtonText="Continue"
        onPrimaryClick={onHide}
      />
    );
  }

  if (error) {
    return (
      <SuccessModal
        showModal={show}
        handleClose={handleClose}
        type="error"
        title="Reassignment Failed"
        message={error}
        primaryButtonText={loading ? "Retrying..." : "Retry"}
        secondaryButtonText="Cancel"
        onPrimaryClick={handleConfirm}
        onSecondaryClick={onHide}
      />
    );
  }

  return (
    <SuccessModal
      showModal={show}
      handleClose={handleClose}
      type="success"
      title="Reassign Teacher"
      message=""
      primaryButtonText={loading ? "Reassigning..." : "Reassign"}
      secondaryButtonText="Cancel"
      onPrimaryClick={handleConfirm}
      onSecondaryClick={onHide}
    >
      {/* Impact warning banner */}
      <div
        className="d-flex align-items-start gap-2 mb-4 p-3"
        style={{
          background: "rgba(255,193,7,0.1)",
          border: "1px solid rgba(255,193,7,0.4)",
          borderRadius: "8px",
          fontSize: "13px",
          color: "#856404",
        }}
      >
        <i
          className="bi bi-exclamation-triangle-fill flex-shrink-0 mt-1"
          style={{ fontSize: "15px" }}
        ></i>
        <span>
          If you reassign this classroom, all{" "}
          <strong>students</strong> currently assigned to this classroom will
          also be transferred to the new teacher.
        </span>
      </div>

      {/* Teacher picker */}
      <div
        ref={dropdownRef}
        style={{ position: "relative", marginBottom: "8px" }}
      >
        <label
          className="form-label"
          style={{ fontWeight: 500, fontSize: "14px" }}
        >
          Select New Teacher
        </label>

        {/* Selected teacher badge */}
        {selectedTeacher ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              background: "#e8f5e9",
              border: "1px solid #a5d6a7",
              borderRadius: "8px",
              fontSize: "14px",
            }}
          >
            <span>
              <i className="bi bi-person-check-fill text-success me-2"></i>
              {teacherName}
              {selectedTeacher.email && (
                <span style={{ color: "#6c757d", marginLeft: 6 }}>
                  ({selectedTeacher.email})
                </span>
              )}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-link text-muted p-0"
              onClick={() => setSelectedTeacher(null)}
              title="Change teacher"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        ) : (
          <>
            {/* Search input */}
            <div style={{ position: "relative" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search teacher by name..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                autoComplete="off"
              />
              <i
                className="bi bi-search"
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aaa",
                  pointerEvents: "none",
                }}
              ></i>
            </div>

            {/* Dropdown list */}
            {dropdownOpen && (
              <ul
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1055,
                  background: "#fff",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  maxHeight: "200px",
                  overflowY: "auto",
                  listStyle: "none",
                  padding: "4px 0",
                  margin: 0,
                }}
              >
                {(() => {
                  const filteredTeachers = (teachers || []).filter(
                    (t) => t.id !== currentTeacherId,
                  );
                  if (filteredTeachers.length > 0) {
                    return (
                      <>
                        {filteredTeachers.map((t) => (
                          <li
                            key={t.id}
                            onClick={() => {
                              setSelectedTeacher(t);
                              setDropdownOpen(false);
                              setSearch("");
                            }}
                            style={{
                              padding: "10px 14px",
                              cursor: "pointer",
                              fontSize: "14px",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f5f5f5")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <div style={{ fontWeight: 500 }}>
                              {t.firstName || t.name} {t.lastName || ""}
                            </div>
                            {t.email && (
                              <div style={{ fontSize: "12px", color: "#6c757d" }}>
                                {t.email}
                              </div>
                            )}
                          </li>
                        ))}

                        {/* Infinite-scroll sentinel */}
                        {teacherMeta &&
                          teacherMeta.current_page < teacherMeta.last_page && (
                            <li
                              ref={observerTarget}
                              style={{ padding: "8px", textAlign: "center" }}
                            >
                              {teacherLoading && (
                                <div
                                  className="spinner-border spinner-border-sm text-secondary"
                                  role="status"
                                ></div>
                              )}
                            </li>
                          )}
                      </>
                    );
                  }
                  if (teacherLoading) {
                    return (
                      <li style={{ padding: "12px", textAlign: "center" }}>
                        <div
                          className="spinner-border spinner-border-sm text-secondary"
                          role="status"
                        ></div>
                      </li>
                    );
                  }
                  return (
                    <li
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        color: "#999",
                        fontSize: "13px",
                      }}
                    >
                      No teachers found
                    </li>
                  );
                })()}
              </ul>
            )}
          </>
        )}
      </div>
    </SuccessModal>
  );
};

export default ReassignTeacherModal;
